/* global URL_MENU */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useNotificationContext } from '@context/NotificationsContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import { useCanSwitchBranch } from '@utils/hooks/useCanSwitchBranch';
import { MobileNavDrawerV4 } from './MobileNavDrawerV4';
import { V4 } from './tokens';
import BranchPicker from './BranchPicker';

const ACCOUNT_PATHS = ['perfil-social', 'mi-cuenta', 'ayuda', 'soporte', 'actualizaciones', 'sugerencias'];

export const MobileTopBarV4: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { commerce } = useGetCommerce();
  const { canSwitch, branches, activeBranchName, switchTo, isRoot } = useCanSwitchBranch();
  const { notifications } = useNotificationContext();
  const hasNotifications = notifications.length > 0;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);

  const isAccountLevel = ACCOUNT_PATHS.some((p) => location.pathname.includes(p));

  const handleNavigate = (path: string) => {
    navigate(`/${path}`);
    setDrawerOpen(false);
  };

  const handleGoToMenu = () => {
    if (commerce.id_path) window.open(`${URL_MENU}/${commerce.id_path}`, '_blank');
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    setDrawerOpen(false);
  };

  return (
    <>
      <header
        style={{
          height: 52,
          background: V4.primary,
          color: V4.white,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px 0 12px',
          gap: 8,
          flexShrink: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
        }}
      >
        {/* Hamburger */}
        <button
          onClick={() => setDrawerOpen(true)}
          style={{
            background: 'transparent',
            border: 0,
            color: V4.white,
            cursor: 'pointer',
            padding: 6,
          }}
          aria-label="Abrir menú"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Branch pill */}
        <button
          onClick={() => canSwitch && !isAccountLevel && setBranchSheetOpen(true)}
          disabled={isAccountLevel || !canSwitch}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'rgba(255,255,255,.12)',
            color: V4.white,
            border: 0,
            borderRadius: 9999,
            padding: '6px 10px 6px 6px',
            cursor: !canSwitch || isAccountLevel ? 'default' : 'pointer',
            fontFamily: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            fontWeight: 600,
            opacity: isAccountLevel ? 0.55 : 1,
          }}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: V4.secondary,
              color: V4.white,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 22s8-5 8-12a8 8 0 1 0-16 0c0 7 8 12 8 12z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span
            style={{
              flex: 1,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {isAccountLevel ? 'Cuenta' : activeBranchName}
          </span>
          {canSwitch && !isAccountLevel && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </button>

        {/* Bell */}
        <button
          style={{
            background: 'transparent',
            border: 0,
            color: V4.white,
            cursor: 'pointer',
            padding: 6,
            position: 'relative',
          }}
          aria-label="Notificaciones"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.7 21a1.95 1.95 0 0 1-3.4 0"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {hasNotifications && (
            <span
              style={{
                position: 'absolute',
                top: 4,
                right: 4,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: V4.danger,
                border: `1.5px solid ${V4.primary}`,
              }}
            />
          )}
        </button>
      </header>

      {/* Branch bottom sheet */}
      {branchSheetOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 250,
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <div
            onClick={() => setBranchSheetOpen(false)}
            style={{ position: 'absolute', inset: 0, background: 'rgba(37,55,70,.45)' }}
          />
          <div
            style={{
              position: 'relative',
              width: '100%',
              background: V4.white,
              borderRadius: '16px 16px 0 0',
              padding: '14px 16px 20px',
              animation: 'plick-drawer-up 240ms cubic-bezier(.2,.8,.2,1)',
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                background: V4.grayLight,
                borderRadius: 2,
                margin: '0 auto 14px',
              }}
            />
            <div style={{ fontSize: 15, fontWeight: 700, color: V4.primary, marginBottom: 10 }}>Cambiar sucursal</div>
            <BranchPicker
              branches={branches}
              current={activeBranchName}
              variant="light"
              autoFocus
              maxHeight="46vh"
              onPick={(n) => {
                switchTo(n);
                setBranchSheetOpen(false);
              }}
            />
          </div>
          <style>
            {'@keyframes plick-drawer-up { from { transform: translateY(100%); } to { transform: translateY(0); } }'}
          </style>
        </div>
      )}

      <MobileNavDrawerV4
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeBranch={activeBranchName}
        branches={canSwitch ? branches.map((b) => b.nombre) : []}
        isAccountLevel={isAccountLevel}
        canChangeBranch={canSwitch}
        showCreateBranch={isRoot}
        onBranchSelect={switchTo}
        activePathname={location.pathname}
        onNavigate={handleNavigate}
        onGoToMenu={handleGoToMenu}
        onLogout={handleLogout}
        commerceName={commerce.nombre ?? ''}
        userName={user.username}
        commerceLogotipo={commerce.logotipo ?? undefined}
      />
    </>
  );
};
